// New experimental functionality; limited to segment flag compoundSegments
import { operators as o } from '@pendo/aggregations';
import { hasSegmentFlag } from '@/data/flags/local-flags';

const ACCOUNT_KINDS = new Set(['account', 'parentAccount']);

function isAccountKind (kind) {
    return ACCOUNT_KINDS.has(kind);
}

function toInlineFilters (conditions) {
    return conditions.length === 1 ? conditions : [{ or: conditions }];
}

function createAdhocPipeline (selectedVisitorType) {
    if (!selectedVisitorType) {
        return {};
    }

    return {
        adhocPipeline: o.pipeline(
            o.sources.visitors({ visitorId: null }),
            o.identified(selectedVisitorType),
            o.select({ visitorId: 'visitorId' }),
        ),
        adhocPipelineName:
            selectedVisitorType === 'visitorId'
                ? 'inlineIdentifiedVisitorsOnly'
                : 'inlineAnonymousVisitorsOnly',
    };
}

function mergeInline (acc, inlineName, filters) {
    acc.inlines[inlineName] = acc.inlines[inlineName] || [];
    acc.inlines[inlineName] = [...acc.inlines[inlineName], ...filters];
}

function createInlineMerger () {
    let mergedInline;
    let mergedInlineAccount;

    function ensureMergedInline (acc, kind, inlineName, inlineAccountName) {
        if (isAccountKind(kind)) {
            if (!mergedInlineAccount) {
                mergedInlineAccount = inlineAccountName;
                acc.compounds.push(inlineAccountName);
            }

            return mergedInlineAccount;
        }

        if (!mergedInline) {
            mergedInline = inlineName;
            acc.compounds.push(inlineName);
        }

        return mergedInline;
    }

    return function mergeNonSegmentFilters (acc, kind, inlineName, inlineAccountName, conditions) {
        const targetInline = ensureMergedInline(acc, kind, inlineName, inlineAccountName);
        mergeInline(acc, targetInline, toInlineFilters(conditions));
    };
}

function getOrConditionGroups (filter) {
    const segmentConditions = filter.or.filter((condition) => condition.schema === 'segment');
    const accountConditions = filter.or.filter((condition) => isAccountKind(condition.kind));

    let regularKind = 'visitor';
    let regularConditions = filter.or.filter(
        (condition) => condition.schema !== 'segment' && !isAccountKind(condition.kind),
    );

    let mixedKindOrConditions = false;

    if (!regularConditions.length) {
        regularConditions = accountConditions;
        regularKind = 'account';
    } else if (accountConditions.length) {
        mixedKindOrConditions = true;
    }

    return {
        segmentConditions,
        accountConditions,
        regularConditions,
        regularKind,
        mixedKindOrConditions,
        hasSegments: segmentConditions.length > 0,
    };
}

function handleOrFilter (acc, filter, inlineName, inlineAccountName, mergeNonSegmentFilters) {
    const {
        segmentConditions,
        accountConditions,
        regularConditions,
        regularKind,
        mixedKindOrConditions,
        hasSegments,
    } = getOrConditionGroups(filter);

    if (!regularConditions.length) {
        acc.compounds.push(segmentConditions);

        return acc;
    }

    if (!hasSegments && !mixedKindOrConditions) {
        mergeNonSegmentFilters(acc, regularKind, inlineName, inlineAccountName, regularConditions);

        return acc;
    }

    acc.inlines[inlineName] = toInlineFilters(regularConditions);

    const compounds = [...segmentConditions, inlineName];

    if (mixedKindOrConditions) {
        acc.inlines[inlineAccountName] = toInlineFilters(accountConditions);
        compounds.push(inlineAccountName);
    }

    acc.compounds.push(compounds);

    return acc;
}

function handleSingleFilter (acc, filter, inlineName, inlineAccountName, mergeNonSegmentFilters) {
    if (filter.schema === 'segment') {
        acc.compounds.push([filter]);

        return acc;
    }

    if (isAccountKind(filter.kind)) {
        mergeNonSegmentFilters(acc, 'account', inlineName, inlineAccountName, [filter]);

        return acc;
    }

    mergeNonSegmentFilters(acc, 'visitor', inlineName, inlineAccountName, [filter]);

    return acc;
}

function addAdhocPipeline (result, adhocPipeline, adhocPipelineName) {
    if (!adhocPipeline) {
        return result;
    }

    result.inlines[adhocPipelineName] = adhocPipeline;
    result.compounds.push(adhocPipelineName);

    return result;
}

export function buildCompoundPipelines (filters, advancedOptions = {}) {
    const { adhocPipeline, adhocPipelineName } = createAdhocPipeline(
        advancedOptions.selectedVisitorType,
    );

    const mergeNonSegmentFilters = createInlineMerger();

    const result = filters.reduce(
        (acc, filter, i) => {
            const inlineName = `inline${i}`;
            const inlineAccountName = `inlineAccount${i}`;

            if (filter.or) {
                return handleOrFilter(
                    acc,
                    filter,
                    inlineName,
                    inlineAccountName,
                    mergeNonSegmentFilters,
                );
            }

            return handleSingleFilter(
                acc,
                filter,
                inlineName,
                inlineAccountName,
                mergeNonSegmentFilters,
            );
        },
        {
            inlines: {},
            compounds: [],
        },
    );

    return addAdhocPipeline(result, adhocPipeline, adhocPipelineName);
}

function hasAccountOrParentAccountRules (filters) {
    return filters.some((filter) => {
        if (filter.or) {
            return hasAccountOrParentAccountRules(filter.or);
        }

        return isAccountKind(filter.kind);
    });
}

function hasVisitorRules (filters) {
    return filters.some((filter) => {
        if (filter.or) {
            return hasVisitorRules(filter.or);
        }

        return !isAccountKind(filter.kind) && filter.schema !== 'segment';
    });
}

function hasNestedSegmentRules (filters) {
    return filters.some((filter) => {
        return filter.schema === 'segment' || filter.or?.some((rule) => rule.schema === 'segment');
    });
}

export function isCompoundSegment (segment) {
    const hasCompoundSegmentsFlag = hasSegmentFlag('compoundSegments');
    const { filters } = segment.definition;

    return hasNestedSegmentRules(filters) || (hasCompoundSegmentsFlag && hasAccountOrParentAccountRules(filters) && hasVisitorRules(filters));
}
